import { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import type { Database } from '../database.types';

type Company = Database['public']['Tables']['companies']['Row'] & {
  metadata?: {
    enableHeaderFeatures?: boolean;
    [key: string]: any;
  };
  website?: string | null;
  description?: string | null;
};

export function useCompany() {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    async function fetchCompany() {
      try {
        if (!initialized) {
          setInitialized(true);
          return;
        }

        // First check if user is authenticated
        const { data: { session }, error: authError } = await supabase.auth.getSession();
        
        if (authError) {
          console.error('Auth error:', authError);
          setCompany(null);
          setLoading(false);
          return;
        }

        if (!session) {
          setCompany(null);
          setLoading(false);
          return;
        }

        // Then fetch company data with email from auth session
        const { data, error: companyError } = await supabase
          .from('companies')
          .select('*, company_users!inner(*)')
          .eq('id', session.user.id)
          .single();

        if (companyError) {
          // Only set error for non-404 errors
          if (!companyError.message.includes('PGRST116')) {
            console.error('Company fetch error:', companyError);
            setError(companyError.message);
          }
          setCompany(null);
        } else {
          // Ensure metadata is initialized with default values
          const companyWithDefaults = {
            ...data,
            metadata: {
              enableHeaderFeatures: false,
              ...(data?.metadata || {})
            },
            // Include email from auth session
            email: session.user.email
          };
          setCompany(companyWithDefaults);
          setError(null);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        setCompany(null);
      } finally {
        setLoading(false);
      }
    }

    fetchCompany();

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setCompany(null);
        setError(null);
      } else if (event === 'SIGNED_IN' && session) {
        fetchCompany();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [initialized]);

  return { company, loading, error };
}